import EventEmitter from "eventemitter3";
import { Data } from "../../../../../models/data";
import { Context } from "../../../../../models/context";
import { IDataModifierDisplayGroup } from "../../../../../models/data.interface";
import { SegmentedButton } from "../../../../shared/SegmentedButton/segmented-button";
import { Util } from "../../../../../utils/util";
import { ISegmentedButtonData } from "../../../../shared/SegmentedButton/segmented-button.interface";
import { LocalizationService } from "../../../../../services/localization-service";
import { HalfModifierSelection } from "../../../../../types/enums";
import { EventTypes } from "../../../../../core/event-bus";

export interface ICustomizeItemPremodifierBaseParams {
    modifierDisplayGroup: IDataModifierDisplayGroup,
    modifierId: number,
    isSelected: boolean,
    preModifierId: number,
    halfId: HalfModifierSelection,
    preModifierExceptions: number[],
    modifierName: string
}

interface IModifierPremodifierSelectionState {
    modifierDisplayGroup: IDataModifierDisplayGroup,
    modifierId: number,
    preModifierId: number,
    halfId: HalfModifierSelection,
    availablePreModifiers: number[]
}

export interface ICustomizeItemPreModifierClickEvent {
    modifierDisplayGroupId: number,
    modifierId: number,
    preModifierId: number,
    halfId: HalfModifierSelection
}

export abstract class CustomizeItemPremodifierBase<TParams extends ICustomizeItemPremodifierBaseParams> {
    protected readonly _eventBus: EventEmitter;
    protected readonly _data: Data;
    protected readonly _context: Context;
    protected readonly _localizationService: LocalizationService;
    protected readonly _containerElement: HTMLElement | null;
    protected readonly _selectionState: IModifierPremodifierSelectionState;

    constructor(eventBus: EventEmitter, data: Data, context: Context, localizationService: LocalizationService, params: TParams) {
        this._eventBus = eventBus;
        this._data = data;
        this._context = context;
        this._localizationService = localizationService;

        this._selectionState = this._initSelectionState(params);
        this._containerElement = this._createPremodifierElement(params);
    }

    /**
    * Gets the root element of the pre-modifier bar.
    * 
    * @returns {HTMLElement | null} The container element, or null if not initialized.
    */
    public get element(): HTMLElement | null {
        return this._containerElement;
    }

    public getPreModifierCount(): number {
        return this._selectionState.availablePreModifiers.length;
    }

    /**
     * Initializes internal state for tracking pre-modifier selection.
     *
     * @param {ICustomizeItemPreModifierMultiItem} params - Modifier and pre-modifier IDs.
     * @returns {IModifierPremodifierSelectionState} The initialized selection state.
     */
    private _initSelectionState(params: TParams): IModifierPremodifierSelectionState {
        return {
            modifierDisplayGroup: params.modifierDisplayGroup,
            modifierId: params.modifierId,
            halfId: params.halfId === HalfModifierSelection.WHOLE ? HalfModifierSelection.LEFT : params.halfId,
            preModifierId: params.preModifierId,
            availablePreModifiers: []
        };
    }

    protected abstract _createElement(): HTMLElement;

    protected abstract _createPremodifierElement(params: TParams): HTMLElement | null;

    /**
     * Constructs a segmented button bar for selecting a pre-modifier.
     *
     * @param {number} preModifierId - Currently selected pre-modifier ID.
     * @param {boolean} includeNonePremodifier - Whether to include the "None" option.
     * @param {number[]} preModifierExceptions - IDs to exclude.
     * @param {boolean} hidden - Whether the bar should be hidden.
     * @returns {HTMLElement | null} Rendered element or null if no valid pre-modifiers exist.
     */
    protected _createPreModifiersSelectionBar(
        preModifierId: number,
        includeNonePremodifier: boolean,
        preModifierExceptions: number[],
        hidden: boolean,
        ariaLabel: string,
        buttonIdSuffix: string
    ): HTMLElement | null {
        const preModifiers = this._data.getProperty("PreModifiers");
        if (preModifiers.length === 0)
            return null;

        const segmentedData: ISegmentedButtonData = {
            buttonId: `customize_item_premodifier_${buttonIdSuffix}`,
            ariaGroupLabel: ariaLabel,
            buttons: []
        };

        for (const preModifier of preModifiers) {
            const isExcluded = preModifierExceptions.includes(preModifier.PreModifierId);
            const isNone = preModifier.IsNone;

            if (isExcluded || (isNone && !includeNonePremodifier))
                continue;

            if (!includeNonePremodifier && preModifier.PreModifierId === this._data.getProperty("NonePreModifierId"))
                continue;

            this._selectionState.availablePreModifiers.push(preModifier.PreModifierId);

            const name = this._localizationService.GetName(preModifier.Names);
            const isSelected = preModifier.PreModifierId === preModifierId;

            segmentedData.buttons.push({
                id: preModifier.PreModifierId,
                text: name,
                selected: isSelected,
                function: (_event: Event): boolean => {
                    return this._preModifierOnChange(preModifier.PreModifierId);
                }
            });
        }

        const segmentedButton = new SegmentedButton(segmentedData, this._localizationService);
        const div = this._createElement();
        div.appendChild(segmentedButton.element);

        if (hidden)
            Util.hideElement(div);

        return div;
    }

    /**
     * Handles when the user selects a different pre-modifier.
     * Emits an event to update the UI and backend state.
     *
     * @param {number} preModifierId - The new pre-modifier selected.
     * @returns {boolean} True if the selection changed; false if no change.
     */
    private readonly _preModifierOnChange = (preModifierId: number): boolean => {
        if (this._selectionState.preModifierId === preModifierId)
            return false;

        this._selectionState.preModifierId = preModifierId;

        this._eventBus.emit(EventTypes.CUSTOMIZE_ITEM_MODIFIER_PREMODIFIER_CLICKED, {
            modifierDisplayGroupId: this._selectionState.modifierDisplayGroup.ModifierDisplayGroupId,
            modifierId: this._selectionState.modifierId,
            halfId: this._selectionState.halfId,
            preModifierId
        } as ICustomizeItemPreModifierClickEvent);
        return true;
    }
}